import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import i18n from '../i18n.js'

// Passwordless email-OTP login.
//
// Two-step flow in a single page:
//   STEP 1  user enters email (+ name + language pref if first-time).
//           We send /api/auth/request-code; backend tells us is_new_user.
//   STEP 2  user enters the 6-digit code we just emailed them. We send
//           /api/auth/verify-code; on success the session is alive and we
//           redirect to wherever they came from (default '/').
//
// The same page handles both login and signup — if the email is new, we
// also ask for name (already collected in step 1 so the backend can issue
// the code with the future-user's metadata attached).

const STEP_EMAIL = 'email'
const STEP_CODE  = 'code'

export function Login() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser, user } = useAuth()

  const from = location.state?.from?.pathname || '/'

  const [step, setStep]         = useState(STEP_EMAIL)
  const [email, setEmail]       = useState('')
  const [name, setName]         = useState('')
  const [language, setLanguage] = useState(i18n.language?.startsWith('en') ? 'en' : 'ro')
  const [code, setCode]         = useState('')
  const [isNewUser, setIsNew]   = useState(false)
  const [showName, setShowName] = useState(false)  // ask for name once user types email
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState(null)
  const [cooldown, setCooldown] = useState(0)      // seconds until you can resend
  const [info, setInfo]         = useState(null)

  // If already authenticated (e.g. user typed /login while logged in), bounce.
  useEffect(() => {
    if (user) navigate(from, { replace: true })
  }, [user, from, navigate])

  // Cooldown countdown for the "Resend code" button.
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => clearTimeout(t)
  }, [cooldown])

  // Auto-submit the code as soon as 6 digits are present. Skipped when we're
  // already loading or when the same code has already failed (so the user
  // doesn't get stuck in a loop hammering verify with a bad code).
  useEffect(() => {
    if (step !== STEP_CODE) return
    // Auto-submit the standard 6-digit OTP, or a 12-digit master code.
    if (code.length !== 6 && code.length !== 12) return
    if (loading) return
    // Don't re-fire a failed 6-digit attempt; a 12-digit master code still
    // auto-submits even after a failed 6-digit pass-through.
    if ((error === 'invalid_code' || error === 'too_many_attempts') && code.length === 6) return
    submitCode()
    // submitCode is defined below; it's stable for our purposes (only reads
    // `email` + `code` from state) — eslint disable to keep deps simple.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step, loading, error])

  async function submitEmail(e) {
    e.preventDefault()
    setError(null); setInfo(null)
    const payload = { email: email.trim().toLowerCase() }
    // Try once without name. If backend says name_required, surface the
    // name field and the user resubmits. Avoids forcing existing users to
    // re-enter their name every login.
    if (showName) {
      if (name.trim().length < 2) { setError('name_required'); return }
      payload.name = name.trim()
      payload.language = language
    }
    setLoading(true)
    try {
      const r = await api.auth.requestCode(payload)
      setIsNew(!!r.is_new_user)
      setCooldown(r.cooldown_remaining_seconds || 60)
      setStep(STEP_CODE)
      setInfo('code_sent')
    } catch (err) {
      if (err.code === 'name_required') {
        // First-time signup needs a name — reveal the field and stay on step 1.
        setShowName(true)
        setError('name_required_first_visit')
      } else if (err.code === 'cooldown_active') {
        setCooldown(err.retryAfterSeconds || 60)
        setError('cooldown_active')
      } else {
        setError(err.code || 'server')
      }
    } finally {
      setLoading(false)
    }
  }

  async function submitCode(e) {
    // Called from the form's onSubmit AND from the auto-submit-at-6-digits
    // effect, so the event is optional.
    e?.preventDefault?.()
    setError(null); setInfo(null)
    const trimmed = code.replace(/\s+/g, '')
    if (!trimmed) { setError('missing_fields'); return }
    setLoading(true)
    try {
      const r = await api.auth.verifyCode({ email: email.trim().toLowerCase(), code: trimmed })
      setUser(r.user)
      navigate(from, { replace: true })
    } catch (err) {
      setError(err.code || 'server')
    } finally {
      setLoading(false)
    }
  }

  async function resend() {
    if (cooldown > 0 || loading) return
    setError(null); setInfo(null); setLoading(true)
    try {
      const payload = { email: email.trim().toLowerCase() }
      if (isNewUser) { payload.name = name.trim(); payload.language = language }
      const r = await api.auth.requestCode(payload)
      setCooldown(r.cooldown_remaining_seconds || 60)
      setInfo('code_resent')
    } catch (err) {
      if (err.code === 'cooldown_active') {
        setCooldown(err.retryAfterSeconds || 60)
      }
      setError(err.code || 'server')
    } finally {
      setLoading(false)
    }
  }

  function startOver() {
    setStep(STEP_EMAIL)
    setCode('')
    setError(null); setInfo(null)
  }

  return (
    <main className="container">
      <section className="card form-card">
        <h1>{step === STEP_EMAIL ? t('auth.login_title') : t('auth.code_title')}</h1>

        {step === STEP_EMAIL && (
          <>
            <p className="muted">{t('auth.email_step_intro')}</p>
            <form onSubmit={submitEmail} noValidate>
              <label>
                <span>{t('auth.email')}</span>
                <input type="email" value={email} required autoFocus
                       name="email" autoComplete="email"
                       onChange={(e) => setEmail(e.target.value)} />
              </label>
              {showName && (
                <>
                  <p className="muted small">{t('auth.name_first_visit_hint')}</p>
                  <label>
                    <span>{t('auth.name')}</span>
                    <input type="text" value={name} required minLength={2}
                           name="name" autoComplete="name"
                           onChange={(e) => setName(e.target.value)} />
                  </label>
                  <label>
                    <span>{t('auth.language_pref')}</span>
                    <select value={language} onChange={(e) => setLanguage(e.target.value)}>
                      <option value="ro">{t('common.language_ro')}</option>
                      <option value="en">{t('common.language_en')}</option>
                    </select>
                  </label>
                </>
              )}
              <AuthAlerts error={error} info={info} cooldown={cooldown} />
              <div className="form-actions">
                <button type="submit" disabled={loading}>
                  {loading ? t('common.loading') : t('auth.send_code')}
                </button>
              </div>
            </form>
          </>
        )}

        {step === STEP_CODE && (
          <>
            <p className="muted">{t('auth.code_sent_to', { email })}</p>
            <form onSubmit={submitCode} noValidate>
              <label>
                <span>{t('auth.code_label')}</span>
                <input type="text" inputMode="numeric" autoComplete="one-time-code"
                       maxLength={12} value={code} required autoFocus
                       onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              </label>
              <AuthAlerts error={error} info={info} cooldown={cooldown} />
              <div className="form-actions">
                <button type="submit" disabled={loading || code.length < 6}>
                  {loading ? t('common.loading') : t('auth.verify_and_signin')}
                </button>
                <button type="button" className="link-button"
                        onClick={resend} disabled={cooldown > 0 || loading}>
                  {cooldown > 0
                    ? t('auth.resend_in', { seconds: cooldown })
                    : t('auth.resend')}
                </button>
                <button type="button" className="link-button" onClick={startOver}>
                  {t('auth.use_different_email')}
                </button>
              </div>
            </form>
          </>
        )}
      </section>
    </main>
  )
}

function AuthAlerts({ error, info, cooldown }) {
  const { t } = useTranslation()
  if (!error && !info) return null
  if (error) {
    return (
      <p className="error">
        {t(`auth.errors.${error}`, {
          defaultValue: error,
          seconds: cooldown,
        })}
      </p>
    )
  }
  return <p className="flash-ok">{t(`auth.info.${info}`, { defaultValue: info })}</p>
}
